import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

// Progressive-Web-App install prompt.
// - Listens for the beforeinstallprompt event on Chrome / Edge / Android.
// - Shows a small banner. Clicking Install fires the native prompt.
// - Users who dismiss it are remembered via localStorage for 14 days.
const DISMISS_KEY = "dl_pwa_dismissed_at";
const DISMISS_DAYS = 14;

const InstallAppPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    const stillHidden = Date.now() - dismissedAt < DISMISS_DAYS * 24 * 60 * 60 * 1000;
    if (stillHidden) return undefined;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  if (!visible) return null;

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try { await deferredPrompt.userChoice; } catch { /* dismissed */ }
    setDeferredPrompt(null);
    setVisible(false);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed z-40 bottom-3 left-1/2 -translate-x-1/2 md:left-4 md:translate-x-0 max-w-md w-[calc(100%-1.5rem)]"
      role="dialog"
      aria-labelledby="pwa-install-title"
    >
      <div className="bg-white border border-slate-200 rounded-2xl shadow-2xl shadow-blue-900/10 p-3 pr-2 flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-blue-50 text-dl-blue grid place-items-center shrink-0">
          <Download className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div id="pwa-install-title" className="text-sm font-bold text-slate-900">Install DoctorLab on your phone</div>
          <div className="text-xs text-slate-500 truncate">One-tap booking, saved to your home screen.</div>
        </div>
        <button
          onClick={install}
          data-testid="pwa-install-btn"
          className="bg-dl-blue hover:bg-blue-700 text-white text-xs font-semibold rounded-full px-3.5 py-2 shrink-0"
        >
          Install
        </button>
        <button
          onClick={dismiss}
          data-testid="pwa-install-dismiss"
          aria-label="Not now"
          className="h-8 w-8 grid place-items-center text-slate-400 hover:text-slate-800 shrink-0"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default InstallAppPrompt;
